import {
  MUIButton,
  MUIDateTimePicker,
  MUIDialog,
  MUITextField,
} from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import dayjs, { Dayjs } from "dayjs"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useDeleteConfirm } from "../../../../hooks/useDeleteConfirm"
import { useOidsRequests } from "../../../../hooks/useOidsRequests"
import { digitalManagerApi } from "../../../../services/api"
import { OidsRequestGet } from "../../../models/SupplierPortal"
import { downloadBlob } from "../../../utils/downloadBlob"
import {
  dateStringComparator,
  getNiceFormattedDate,
  getNowDateTime,
  getUtcDateTime,
} from "../../../utils/getFormattedDate"
import { EntityRow, EntityTab } from "../../partials/EntityTab"

const MAX_OIDS = 1000
const LOCAL_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm"

const removeDownloadingId =
  (idToRemove: string) =>
  (ids: string[]): string[] =>
    ids.filter((id) => id !== idToRemove)

export function ValidateObjectidDialog({
  validateObjectid,
  setValidateOidsRequest,
  dialogParams,
  reloadOidRequests,
}: {
  readonly validateObjectid: boolean
  readonly setValidateOidsRequest: React.Dispatch<React.SetStateAction<boolean>>
  readonly dialogParams?: EntityRow
  readonly reloadOidRequests: () => void
}) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{ validationDate: string }>({
    mode: "onChange",
  })

  useEffect(() => {
    reset({ validationDate: getNowDateTime() })
  }, [reset])

  const onSubmit = useCallback(
    async (values: { validationDate: string }) => {
      if (!Date.parse(values.validationDate)) {
        toast.error("Invalid date format")
        return
      }
      try {
        const response = await digitalManagerApi.put(
          `/litePanelPro/objectIdsValidation`,
          {},
          {
            params: {
              validationDate: getUtcDateTime(values.validationDate),
              oidsRequestId: dialogParams?.id,
            },
          }
        )
        if (response.status === StatusCodes.OK) {
          setValidateOidsRequest(false)
          toast.success("Oids validated")
          reloadOidRequests()
        }
      } catch {
        toast.error("Cannot validate the oids")
      }
    },
    [dialogParams?.id, reloadOidRequests, setValidateOidsRequest]
  )

  const handleClose = useCallback(() => {
    setValidateOidsRequest(false)
  }, [setValidateOidsRequest])

  const createValidationDateHandler = useCallback(
    (field: ControllerRenderProps<{ validationDate: string }, "validationDate">) =>
      (value: Dayjs | null) => {
        field.onChange(
          value?.isValid() ? value.format(LOCAL_DATE_TIME_FORMAT) : ""
        )
      },
    []
  )

  const renderValidationDateField = useCallback(
    (props: {
      field: ControllerRenderProps<{ validationDate: string }, "validationDate">
    }) => (
      <MUIDateTimePicker
        id="validationDate"
        name={props.field.name}
        size={32}
        value={props.field.value ? dayjs(props.field.value) : null}
        onChange={createValidationDateHandler(props.field)}
        isDisabled={props.field.disabled}
        errors={
          errors.validationDate
            ? {
                validationDate: {
                  message: String(errors.validationDate.message),
                },
              }
            : undefined
        }
      />
    ),
    [errors.validationDate, createValidationDateHandler]
  )

  return (
    <MUIDialog
      id="validate-objectid-dialog"
      variant="information"
      open={validateObjectid}
      onClose={handleClose}
      heading="Indicates the date from which the oids are valid"
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="validationDate"
            control={control}
            rules={{
              required: "Validation date is required",
              validate: (value) =>
                Boolean(value && dayjs(value).isValid()) || "Invalid date",
            }}
            render={renderValidationDateField}
          />
        </form>
      }
      primaryButtonText="Validate"
      onClickPrimaryButton={handleSubmit(onSubmit)}
      isPrimaryButtonDisabled={isSubmitting}
      primaryButtonConfig={{
        isLoading: isSubmitting,
        type: "primary",
        size: 32,
        id: "validate-btn",
      }}
    />
  )
}

export function OpenObjectIdRequestDialog({
  openOidRequest,
  reloadOidRequests,
  setOpenOidRequest,
}: {
  readonly openOidRequest: boolean
  readonly reloadOidRequests: () => void
  readonly setOpenOidRequest: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const validationSchema = z.object({
    numberOfOids: z
      .number()
      .max(MAX_OIDS, `Maximum allowed value is ${MAX_OIDS}`)
      .min(1, "Minimum allowed value is 1"),
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{ numberOfOids: number }>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({ numberOfOids: 0 })
  }, [reset])

  const handleClose = useCallback(() => {
    setOpenOidRequest(false)
  }, [setOpenOidRequest])

  const onSubmit = useCallback(
    async (values: { numberOfOids: number }) => {
      try {
        const response = await digitalManagerApi.post(
          `/litePanelPro/objectIdsRequests`,
          {},
          { params: { numObjectIds: values.numberOfOids } }
        )
        if (response.status === StatusCodes.OK) {
          setOpenOidRequest(false)
          toast.success("Oids request successfully created")
          reloadOidRequests()
        }
      } catch {
        toast.error("Cannot create the Oids request")
      }
    },
    [reloadOidRequests, setOpenOidRequest]
  )

  const createNumberOfOidsHandler = useCallback(
    (field: ControllerRenderProps<{ numberOfOids: number }, "numberOfOids">) =>
      (value: string) => {
        field.onChange(Number(value))
      },
    []
  )

  const renderTextFieldNumberOfOids = useCallback(
    (props: {
      field: ControllerRenderProps<{ numberOfOids: number }, "numberOfOids">
    }) => (
      <MUITextField
        id="numberOfOids"
        name={props.field.name}
        type="number"
        size={32}
        value={props.field.value?.toString() ?? ""}
        onChange={createNumberOfOidsHandler(props.field)}
        onBlur={props.field.onBlur}
        isDisabled={props.field.disabled}
        errors={
          errors.numberOfOids
            ? { numberOfOids: { message: "Number must be between 1 and 1000" } }
            : undefined
        }
      />
    ),
    [errors.numberOfOids, createNumberOfOidsHandler]
  )

  return (
    <MUIDialog
      id="oid-request-dialog"
      variant="information"
      open={openOidRequest}
      onClose={handleClose}
      heading="Indicates the number of oids you want to create"
      content={
        <Controller
          name="numberOfOids"
          control={control}
          render={renderTextFieldNumberOfOids}
        />
      }
      primaryButtonText="Create"
      onClickPrimaryButton={handleSubmit(onSubmit)}
      isPrimaryButtonDisabled={isSubmitting}
      primaryButtonConfig={{
        isLoading: isSubmitting,
        type: "primary",
        size: 32,
        id: "create-oid-btn",
      }}
    />
  )
}

export function ObjectIdRequests() {
  const { oidRequestList, isLoadingOidRequests, reloadOidRequests } = useOidsRequests()
  const [validateOidsRequest, setValidateOidsRequest] = useState(false)
  const [openOidRequest, setOpenOidRequest] = useState(false)
  const [dialogParams, setDialogParams] = useState<EntityRow>()
  const [downloadingIds, setDownloadingIds] = useState<string[]>([])
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()

  const navigate = useNavigate()

  const onValidate = useCallback(
    (row: EntityRow) => {
      const r = row as { id: string }
      setDialogParams({ id: r.id })
      setValidateOidsRequest(true)
    },
    [setDialogParams, setValidateOidsRequest]
  )

  const onDownload = useCallback((row: EntityRow) => {
    const r = row as { id: string }
    setDownloadingIds((prev) =>
      prev.includes(r.id) ? prev : [...prev, r.id]
    )
    const toastId = toast.loading("Downloading the oids...")
    digitalManagerApi
      .get(`/litePanelPro/objectIdsCsv?oidsRequestId=${r.id}`)
      .then((res) => {
        if (res.status === StatusCodes.OK) {
          const fileName = `OidsRequest-${r.id}.csv`
          const blob = new Blob([res.data], { type: "text/csv" })
          downloadBlob(blob, fileName)
          toast.update(toastId, {
            render: "Download completato",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          })
        }
      })
      .catch(() =>
        toast.update(toastId, {
          render: "Cannot download the file",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        })
      )
      .finally(() =>
        setDownloadingIds(removeDownloadingId(r.id))
      )
  }, [])

  const onOpen = useCallback(
    (row: EntityRow) => {
      const r = row as { id: string }
      navigate(`/supplierPortal/objectIdsRequests/${r.id}/objectIds`)
    },
    [navigate]
  )

  const onDelete = useCallback(
    (row: EntityRow) => {
      const r = row as { id: string }
      requestDelete(() => {
        digitalManagerApi
          .delete(`/litePanelPro/objectIdsRequests?requestId=${r.id}`)
          .then((res) => {
            if (res.status === StatusCodes.OK) {
              reloadOidRequests()
              toast.success("Oids request successfully deleted")
            }
          })
          .catch(() => toast.error("Cannot delete the Oids request"))
      })
    },
    [reloadOidRequests, requestDelete]
  )

  const handleActionMenuClick = useCallback(
    (item: { title: string; iconName?: string }, row: EntityRow) => {
      const r = row as unknown as EntityRow
      if (item.title === "Validate") {
        onValidate(r)
      }
      if (item.title === "Download") {
        if (downloadingIds.includes((row as { id: string }).id)) {
          return
        }
        onDownload(r)
      }
      if (item.title === "Open") {
        onOpen(r)
      }
      if (item.title === "Delete") {
        onDelete(r)
      }
    },
    [onValidate, onDownload, onOpen, onDelete, downloadingIds]
  )

  const handleOpenOidRequest = useCallback(() => {
    setOpenOidRequest(true)
  }, [])

  const cols = [
    {
      field: "requestDate",
      headerName: "Request Date",
      sortComparator: dateStringComparator,
    },
    { field: "oidCount", headerName: "Number of OIDs" },
    {
      field: "expireDate",
      headerName: "Expire Date",
      sortComparator: dateStringComparator,
    },
  ]

  const rows: EntityRow[] = [...oidRequestList]
    .sort((a, b) =>
      dateStringComparator(b.requestDate.toString(), a.requestDate.toString())
    )
    .map((o: OidsRequestGet) => ({
      id: o.id,
      requestDate: getNiceFormattedDate(o.requestDate.toString()),
      oidCount: o.oidsCount,
      expireDate: o.expireDate
        ? getNiceFormattedDate(o.expireDate.toString())
        : "Not validated",
    }))

  return (
    <>
      <ValidateObjectidDialog
        validateObjectid={validateOidsRequest}
        setValidateOidsRequest={setValidateOidsRequest}
        reloadOidRequests={reloadOidRequests}
        dialogParams={dialogParams}
      />
      <OpenObjectIdRequestDialog
        openOidRequest={openOidRequest}
        reloadOidRequests={reloadOidRequests}
        setOpenOidRequest={setOpenOidRequest}
      />
      {DeleteConfirmDialog}

      <EntityTab
        rows={rows}
        cols={cols}
        isLoading={isLoadingOidRequests}
        actionItems={[
          { title: "Validate", iconName: "icon_abb_dias icon-abb_Task-List-Check-2" },
          { title: "Download", iconName: "icon_abb_dias icon-abb_download" },
          { title: "Open", iconName: "icon_abb_dias icon-abb_folder-open" },
          { title: "Delete", iconName: "icon_abb_dias icon-abb_delete" },
        ]}
        onActionMenuClick={handleActionMenuClick}
        toolbar={
          <MUIButton
            variant="primary"
            onClickButton={handleOpenOidRequest}
            label="Open ObjectId Request"
            size={32}
          />
        }
      />
    </>
  )
}
